import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _get from 'lodash/get';

import { useFeatures } from '@atc/features';

import { getSimilarListings } from '@/actions/vdp/remoteActions';

/**
 * Custom hook to handle fetching and transforming similar listings
 *
 * @param {Object} options - Hook options
 * @param {Object} options.listing - The current listing object
 * @param {string} options.channel - The current channel
 * @param {boolean} options.isSimilarDealerInventoryOnly - Whether to only fetch listings from the same dealer
 * @param {boolean} options.isSimilarListingsCarouselEnabled - Whether similar listings carousel is enabled
 * @param {Object} options.similarListingsDuck - The Redux duck for similar listings
 * @param {string} options.inventoryImageUrl - The URL for inventory images
 * @param {boolean} options.isConquestable - Whether the listing is conquestable
 * @returns {Object} An object containing transformed similar listings and loading state
 */
export const useSimilarListings = ({
    listing,
    channel,
    isSimilarDealerInventoryOnly,
    isSimilarListingsCarouselEnabled,
    similarListingsDuck,
    inventoryImageUrl,
    isConquestable,
    hasPrivateSellerTypeParam,
}) => {
    const dispatch = useDispatch();
    const [similarListingsFetched, setSimilarListingsFetched] = useState(false);
    const { default_query_params: [enableDefaultQueryParams, { queryParams }] } = useFeatures(['default_query_params']);

    const similarListings = useSelector(similarListingsDuck.selectors.getActiveInventory) ?? [];
    const isSimilarListingsInitialLoad = useSelector(similarListingsDuck.selectors.getInitialLoad);

    // reset inView & similarListingsFetched when changing vdps
    useEffect(() => () => {
        setSimilarListingsFetched(false);
        dispatch(similarListingsDuck.creators.clear());
    }, [listing.id, dispatch, similarListingsDuck.creators]);

    // fetch if component is in view and similarListings not fetched yet
    useEffect(() => {
        if (!similarListingsFetched) {
            dispatch(getSimilarListings({
                listing,
                channel,
                isSimilarListingsCarouselEnabled,
                isSimilarDealerInventoryOnly,
                enableDefaultQueryParams,
                queryParams,
                hasPrivateSellerTypeParam,
            }));
            setSimilarListingsFetched(true);
        }
    }, [listing.id, similarListingsFetched, dispatch, channel, isSimilarListingsCarouselEnabled, isSimilarDealerInventoryOnly, listing, enableDefaultQueryParams, queryParams, hasPrivateSellerTypeParam]);

    // Transform listings to modify image URLs and owner info
    const transformListings = useCallback(({ listings = [] }) => listings.map((originalListing) => {
        const image = _get(originalListing, 'images.sources[0]', null);
        let images;

        if (image?.src) {
            images = {
                ...originalListing.images,
                sources: [
                    {
                        ...image,
                        src: image.src.toString().replace(/images.qa.autotrader.com|images.autotrader.com/, inventoryImageUrl),
                    },
                ],
            };
        }

        return {
            ...originalListing,
            ...(images && { images }),
            owner: {
                ...originalListing.owner,
                name: isConquestable && originalListing?.owner?.name,
            },
        };
    }), [isConquestable, inventoryImageUrl]);

    const transformedSimilarListings = transformListings({
        listings: similarListings,
    });

    return {
        similarListings: transformedSimilarListings,
        isSimilarListingsInitialLoad,
        clearSimilarListings: () => dispatch(similarListingsDuck.creators.clear()),
    };
};

export default useSimilarListings;
